import * as React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { alpha } from '@mui/material/styles';

import { Navigate, useNavigate } from 'react-router-dom';

import apiClient, { shouldRedirectToLogin } from '../services/api-client';
import AppConfig from '../services/app-config';
import CustomSnackbar from '../components/CustomSnackbar';

export default function NewVisit({ setNavbarState, navbarState }) {
  const navigate = useNavigate();

  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [loading, setLoading] = React.useState(true);

  const [rules, setRules] = React.useState({
    vehicle: true,
    walking: true,
    default_lifespan_hours: 24,
    allow_multiple_entries: true,
    allow_longer_lifespan: true,
    max_lifespan_hours: 168,
    days_array: ['1 Dia'],
    default: { allow_multiple_entries: true },
  });

  const [visitorName, setVisitorName] = React.useState('');
  const [visitorNameSuggestions, setVisitorNameSuggestions] = React.useState([]);
  const [visitorNameSuggestionsLoading, setVisitorNameSuggestionsLoading] = React.useState(false);
  const [walking, setWalking] = React.useState(true);
  const [vehicle, setVehicle] = React.useState(false);
  const [vehiclePlate, setVehiclePlate] = React.useState('');

  const [notes, setNotes] = React.useState('');
  const [visitDate, setVisitDate] = React.useState('');
  const [visitTime, setVisitTime] = React.useState('');

  const [showAdvanceOpts, setShowAdvanceOpts] = React.useState(false);
  const [showGranular, setShowGranular] = React.useState(false);
  const [advanceOpts, setAdvanceOpts] = React.useState({ allow_multiple_entries: false, max_lifespan_hours: 24 });

  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const filteredVisitorNameSuggestions = React.useMemo(() => {
    const q = visitorName.trim().toLowerCase();
    if (q.length < 2) return [];
    return visitorNameSuggestions.filter((s) => typeof s === 'string' && s.toLowerCase().includes(q));
  }, [visitorName, visitorNameSuggestions]);

  const visitorNameHasError = visitorName.trim() !== '' && visitorName.trim().split(/\s+/).length < 2;

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: false, showOptions: false, showSearch: false });
  }, []);

  React.useEffect(() => {
    setVisitorNameSuggestionsLoading(true);
    apiClient
      .get('suggestions/visits/names', {}, { action: 'fetch_visit_name_suggestions' })
      .then((response) => {
        if (response.status === 200) {
          const suggestions = Array.isArray(response.data?.data) ? response.data.data : [];
          setVisitorNameSuggestions(suggestions.filter((s) => typeof s === 'string'));
        } else if (response.status === 401) {
          setRedirect({ trigger: true, route: '/login' });
        } else {
          setVisitorNameSuggestions([]);
        }
      })
      .catch((error) => {
        if (shouldRedirectToLogin(error)) {
          setRedirect({ trigger: true, route: '/login' });
        } else {
          setVisitorNameSuggestions([]);
        }
      })
      .finally(() => setVisitorNameSuggestionsLoading(false));
  }, []);

  // Helper function to format timespan label
  const formatTimespan = (hours) => {
    if (hours < 24) {
      return `${hours} horas`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days} dia${days > 1 ? 's' : ''}`;
    }
  };

  React.useEffect(() => {
    setLoading(true);
    apiClient
      .get('visit/rules', {}, { action: 'fetch_visit_rules' })
      .then((response) => {
        if (response.status === 200) {
          const data = response.data.data;
          const maxHours = data.max_lifespan_hours || 24;
          const defaultLifespanHours = data.default_lifespan_hours || 24;
          
          // Build timespan options: hours for <24, days for >=24
          const timespanOptions = [];
          
          // Add hour-based options (less than 24 hours)
          const hourIncrement = defaultLifespanHours >= 12 ? 12 : (defaultLifespanHours >= 8 ? 8 : defaultLifespanHours);
          for (let h = defaultLifespanHours; h < 24 && h <= maxHours; h += hourIncrement) {
            timespanOptions.push({ value: h, label: `${h} horas` });
          }
          
          // Add day-based options (24 hours and above)
          if (maxHours >= 24) {
            const maxDays = Math.floor(maxHours / 24);
            for (let d = 1; d <= maxDays; d++) {
              const h = d * 24;
              if (h <= maxHours) {
                timespanOptions.push({ value: h, label: `${d} dia${d > 1 ? 's' : ''}` });
              }
            }
          }
          
          // Ensure max value is included
          const lastValue = timespanOptions[timespanOptions.length - 1]?.value || defaultLifespanHours;
          if (lastValue < maxHours) {
            const remainingDays = Math.floor(maxHours / 24);
            if (remainingDays >= 1 && !timespanOptions.find(o => o.value === remainingDays * 24)) {
              timespanOptions.push({ value: remainingDays * 24, label: `${remainingDays} dia${remainingDays > 1 ? 's' : ''}` });
            }
          }
          
          setRules({ ...data, timespan_options: timespanOptions });
          const canShow = !!data.allow_longer_lifespan || !!data?.default?.allow_multiple_entries;
          setShowAdvanceOpts(canShow);
          setShowGranular(false);
          // Use default_lifespan_hours from org rules directly
          setAdvanceOpts({ allow_multiple_entries: false, max_lifespan_hours: defaultLifespanHours });
          setLoading(false);
        } else if (response.status === 401) {
          setRedirect({ trigger: true, route: '/login' });
        }
      })
      .catch((error) => {
        setLoading(false);
        if (shouldRedirectToLogin(error)) {
          setRedirect({ trigger: true, route: '/login' });
        } else {
          setSnackbar({ open: true, message: 'Error al obtener reglas para crear accesos.', severity: 'error' });
        }
      });
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreate = () => {
    if (visitorName.trim() === '' || (!walking && !vehicle)) {
      setSnackbar({ open: true, message: 'Ingrese el nombre y asegurese de marcar el tipo de acceso.', severity: 'warning' });
      return;
    }
    if (visitorName.split(' ').length < 2) {
      setSnackbar({ open: true, message: 'Por favor agregar al menos un nombre y un apellido.', severity: 'warning' });
      return;
    }
    if (vehicle && !vehiclePlate && AppConfig.get('force_resident_vehicle_plate_input')) {
      setSnackbar({ open: true, message: 'Por favor ingrese la placa del vehiculo.', severity: 'warning' });
      return;
    }

    const visitorNameCamelCase = visitorName
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    const data = {
      visitor_name: visitorNameCamelCase,
      walking,
      vehicle,
      vehicle_plate: vehiclePlate,
      max_lifespan_hours: advanceOpts.max_lifespan_hours,
      allow_multiple_entries: advanceOpts.allow_multiple_entries,
      notes: notes.trim() || null,
    };

    // Add valid_from timestamp if date/time is set in Control Granular
    if (visitDate && visitTime) {
      const dateTimeString = `${visitDate}T${visitTime}`;
      const validFromDate = new Date(dateTimeString);
      if (!isNaN(validFromDate.getTime())) {
        data.valid_from = Math.floor(validFromDate.getTime() / 1000);
      }
    }

    setLoading(true);
    apiClient
      .post('visit/create', data, {}, { action: 'create_visit' })
      .then((response) => {
        if (response.status === 200) {
          setSnackbar({ open: true, message: 'Acceso Creado Exitosamente', severity: 'success' });
          setRedirect({ trigger: true, route: `/compartir?code=${response.data.data.code}` });
        } else if (response.status === 401) {
          setRedirect({ trigger: true, route: '/login' });
        } else {
          setSnackbar({ open: true, message: 'Error al crear acceso, intente de nuevo en un momento.', severity: 'error' });
        }
      })
      .catch(() => {
        setSnackbar({ open: true, message: 'Error al crear acceso, intente de nuevo en un momento.', severity: 'error' });
      })
      .finally(() => setLoading(false));
  };

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  return (
    <Container maxWidth="sm" sx={{ pb: 16 }}>
      <CustomSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={handleSnackbarClose} />

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.default',
          pt: 2,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              width: 40,
              height: 40,
              ml: -1,
              color: 'text.primary',
              '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08) },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 28 }} />
          </IconButton>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>Nueva Visita</Typography>
        </Box>
      </Box>

      <Box sx={{ px: 0, pt: 3, pb: 6 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxWidth: 520, mx: 'auto' }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  Nombre del Visitante
                </Typography>
                <Autocomplete
                  freeSolo
                  options={filteredVisitorNameSuggestions}
                  loading={visitorNameSuggestionsLoading}
                  inputValue={visitorName}
                  onInputChange={(event, newInputValue) => {
                    if (typeof newInputValue === 'string') {
                      setVisitorName(newInputValue);
                    }
                  }}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      setVisitorName(newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Ej. Juan Pérez"
                      error={visitorNameHasError}
                      helperText={visitorNameHasError ? 'Ingrese al menos un nombre y un apellido' : ''}
                      InputProps={{
                        ...params.InputProps,
                        sx: { height: 56, fontWeight: 600 },
                        endAdornment: (
                          <>
                            {visitorNameSuggestionsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  Tipo de Visita
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Button
                    variant={walking ? 'contained' : 'outlined'}
                    color={walking ? 'primary' : 'inherit'}
                    onClick={() => {
                      setWalking(true);
                      setVehicle(false);
                    }}
                    sx={{
                      height: 92,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      borderWidth: 2,
                      fontWeight: 900,
                    }}
                  >
                    <DirectionsWalkIcon sx={{ fontSize: 28 }} />
                    Peatonal
                  </Button>

                  <Button
                    variant={vehicle ? 'contained' : 'outlined'}
                    color={vehicle ? 'primary' : 'inherit'}
                    onClick={() => {
                      setVehicle(true);
                      setWalking(false);
                    }}
                    sx={{
                      height: 92,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      borderWidth: 2,
                      fontWeight: 900,
                    }}
                  >
                    <DirectionsCarIcon sx={{ fontSize: 28 }} />
                    Vehicular
                  </Button>
                </Box>
              </Box>

              {vehicle && (
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                    Placa del vehiculo
                  </Typography>
                  <TextField
                    fullWidth
                    value={vehiclePlate}
                    onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                    InputProps={{ sx: { height: 56, fontWeight: 600 } }}
                  />
                </Box>
              )}

              {showAdvanceOpts && (
                <Box>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => setShowGranular((prev) => !prev)}
                    sx={{
                      width: '100%',
                      height: 56,
                      justifyContent: 'space-between',
                      px: 2,
                      bgcolor: 'rgba(0,0,0,0.02)',
                      borderColor: 'rgba(0,0,0,0.06)',
                    }}
                    startIcon={<TuneIcon />}
                    endIcon={<ExpandMoreIcon sx={{ transform: showGranular ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, justifyContent: 'flex-start' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Control Granular</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', mr: 1 }}>
                        {formatTimespan(advanceOpts.max_lifespan_hours)}
                      </Typography>
                    </Box>
                  </Button>

                  <Collapse in={showGranular}>
                    <Box sx={{ pt: 2 }}>
                      <Stack spacing={2}>
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                            Fecha y Hora
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                              type="date"
                              fullWidth
                              value={visitDate}
                              onChange={(e) => setVisitDate(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ sx: { height: 56, fontWeight: 600, textTransform: 'uppercase', fontSize: 12 } }}
                            />
                            <TextField
                              type="time"
                              value={visitTime}
                              onChange={(e) => setVisitTime(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: '40%' }}
                              InputProps={{ sx: { height: 56, fontWeight: 600, fontSize: 12 } }}
                            />
                          </Box>
                        </Box>

                        {rules.allow_longer_lifespan && (
                          <FormControl fullWidth>
                            <InputLabel id="timespan-select-label">Validez</InputLabel>
                            <Select
                              labelId="timespan-select-label"
                              value={advanceOpts.max_lifespan_hours}
                              label="Validez"
                              onChange={(event) =>
                                setAdvanceOpts({
                                  allow_multiple_entries: event.target.value !== rules.default_lifespan_hours,
                                  max_lifespan_hours: event.target.value,
                                })
                              }
                            >
                              {rules.timespan_options && rules.timespan_options.map((option, index) => (
                                <MenuItem key={index} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {advanceOpts.max_lifespan_hours === rules.default_lifespan_hours && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={advanceOpts.allow_multiple_entries}
                                onChange={() =>
                                  setAdvanceOpts({ ...advanceOpts, allow_multiple_entries: !advanceOpts.allow_multiple_entries })
                                }
                              />
                            }
                            label="Permitir Multiples Accesos"
                          />
                        )}
                      </Stack>
                    </Box>
                  </Collapse>
                </Box>
              )}

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  Notas Adicionales
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Instrucciones para garita..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 88,
          left: 'calc(var(--app-drawer-width, 0px) + (100vw - var(--app-drawer-width, 0px)) / 2)',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 600,
          px: 2,
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          onClick={handleCreate}
          sx={{ height: 56, fontWeight: 900, pointerEvents: 'auto' }}
        >
          Generar Invitación
        </Button>
      </Box>
    </Container>
  );
}
